'use client'

import { Info } from 'lucide-react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { AnimatedCurrency } from './animated-currency'

interface SavingsLineProps {
  label: string
  amount: number
  explanation: string
}

/**
 * A savings figure whose label explains itself on hover.
 *
 * Three numbers on a checkout are called "hemat" and none of them is a discount in the billing
 * sense: the tier and the term ARE the price. Buyers read them as promotions and then ask why the
 * invoice does not mention them, so each one carries its own explanation rather than a shared
 * footnote nobody connects to the right line.
 *
 * Shared by the NEW and RENEWAL forms, which quote the same two savings against the same rate card.
 */
export function SavingsLine({ label, amount, explanation }: SavingsLineProps) {
  return (
    <p
      className={cn(
        typographyVariants({ variant: 'body-md' }),
        'srd:text-purple-100 srd:flex srd:items-center srd:justify-end srd:gap-x-1',
      )}
    >
      {label}

      <Tooltip>
        <TooltipTrigger type='button' aria-label={`Apa itu ${label}`} className='srd:shrink-0'>
          <Info className='srd:size-4' />
        </TooltipTrigger>
        <TooltipContent className='srd:max-w-[240px] srd:text-left'>{explanation}</TooltipContent>
      </Tooltip>

      <AnimatedCurrency
        value={amount}
        className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-purple-100')}
      />
    </p>
  )
}

/**
 * The two explanations, kept together so the NEW and RENEWAL cards cannot describe them differently.
 *
 * There is no `total` entry. "Total hemat" in the sticky bar is a figure, not a claim that needs
 * defending — the lines it is built from each carry their own explanation right above it.
 */
export const SAVINGS_EXPLANATION = {
  term: 'Harga per kursi per bulan makin murah kalau paketnya lebih panjang. Ini selisihnya dibanding paket terpendek untuk jumlah kursi yang sama.',
  tier: 'Harga per kursi turun sendiri saat jumlah kursi naik. Ini selisihnya dibanding harga untuk jumlah kursi paling sedikit di paket yang sama.',
} as const
